import $ from 'jquery';
import Ps from 'perfect-scrollbar';
import debounce from './utils/debounce';

require('intersection-observer');

class ChapterNav {
    constructor () {

        this.$document            = $(document);
        this.$window              = $(window);
        this.$body                = $('body');
        this.$page                = $('.js-page');
        this.$header              = $('.js-header');
        this.$footer              = $('.js-footer');
        this.$footerPlaceholder   = $('.js-footer-placeholder');
        this.$pageHeader          = $('.js-page-header');
        this.$fixedFooter         = $('.js-fixed-footer');
        this.$button              = $('.js-chapter-button');
        this.$chapterNav          = $('.js-chapter-nav');
        this.$chapterNavLink      = $('.js-chapter-nav-link');
        this.$chapterNavShare     = $('.js-chapter-nav-share');
        this.$chapterContent      = $('.js-chapter-content');
        this.$progressBar         = $('.js-progress');
        this.$chapterHeadings     = $('.js-chapter-heading');
        this.mobileBreakpoint     = 767;
        this.currentActiveChapter = 0; // Stores the currently active chapter heading.

        this.$button.on('click', $.proxy(this.toggleOpen, this));
        this.$chapterNavLink.on('click', $.proxy(this.navClose, this));

        this.$window.on('resize', debounce(() => {
            this.updateScrollbars();
        }));

        this.$window.on('load resize', debounce(() => {
            // Close nav on document click
            this.$document.on('click', $.proxy(this.documentClick, this));

            if (this.$window.width() > this.mobileBreakpoint) {
                this.$document.keyup((e) => {
                    // Escape Key
                    if (e.keyCode === 27) {
                        this.navClose();
                    }
                });
            }
        }, 300));

        // Scrollbars
        //----------------------------------------
        this.chapterScrollbars = [];

        this.addScrollbars();

        // Progress bar
        //----------------------------------------
        this.progressBarMax();

        // Chapter nav
        //----------------------------------------
        if (this.$fixedFooter.length) {
            this.observeNav();
            this.observeFooter();
        }

        this.$window.on('scroll', () => {
            this.progressBarValue();
            this.highlightActiveChapter();
        });
    }

    highlightActiveChapter () {
        // ***** Helper functions *****
        // Sets a given chapter nav link as active.
        const makeActive      = (link) => this.$chapterNavLink[link].classList.add('is-active');
        // Sets a given chapter nav link as inactive.
        const removeActive    = (link) => this.$chapterNavLink[link].classList.remove('is-active');
        // Sets all chapter nav links as inactive.
        const removeAllActive = () => [...Array(this.$chapterHeadings.length).keys()].forEach((link) => removeActive(link));
        // ***** End of helper functions *****
        // Gets the current chapter heading that is in focus by taking the last visible heading.
        const current = this.$chapterHeadings.length - [...this.$chapterHeadings].reverse().findIndex((chapterHeading) => window.scrollY >= chapterHeading.offsetTop + 60) - 1;
        // If the recently fetched chapter heading is not the same as the previously fetched one, set the new chapter heading as active and make the rest inactive.
        if (current < this.$chapterHeadings.length && current !== this.currentActiveChapter) {
            removeAllActive();
            this.currentActiveChapter = current;
            makeActive(current);
        }
    }

    navOpen (e) {
        const $elem = $(e.currentTarget);
        $elem.addClass('is-clicked').attr('aria-expanded', 'true');
        this.$body.addClass('has-overlay has-overlay--chapter-nav scroll-locked');
        this.$page.addClass('overlay-is-open');
        this.$chapterNav.addClass('is-open').attr('aria-hidden', 'false');
        this.$chapterNavShare.addClass('is-hidden');
        this.$fixedFooter.addClass('nav-open overlay-is-open');
        this.$footer.addClass('is-static');
        this.$footerPlaceholder.hide();
    }

    navClose () {
        this.$button.removeClass('is-clicked').attr('aria-expanded', 'false');
        this.$chapterNav.removeClass('is-open').attr('aria-hidden', 'true');
        this.$page.removeClass('overlay-is-open');
        this.$chapterNavShare.removeClass('is-hidden');
        this.$footer.removeClass('is-static');
        this.$footerPlaceholder.show();
        // Wait for the menu animation to finish
        setTimeout(() => {
            this.$fixedFooter.removeClass('nav-open overlay-is-open');
        }, 400);

        if (this.$body.hasClass('has-overlay--chapter-nav')) {
            this.$body.removeClass('has-overlay has-overlay--chapter-nav scroll-locked');
        }
    }

    toggleOpen (e) {
        const $elem = $(e.currentTarget);

        if (!$elem.hasClass('is-clicked')) {
            this.navOpen(e);
        } else {
            this.navClose();
        }
    }

    documentClick (e) {
        const $elem = $(e.target);

        if (!$elem.closest(this.$fixedFooter).length && this.$fixedFooter.hasClass('overlay-is-open')) {
            this.navClose();
        }
    }

    addScrollbars () {
        if (this.$chapterNav.length) {
            this.chapterScrollbars.push(new Ps(this.$chapterNav[0]));
        }
    }

    updateScrollbars () {
        if (this.$chapterNav.length) {
            this.chapterScrollbars.forEach((ps) => ps.update());
        }
    }

    progressBarMax () {
        /* Set the max scrollable area */
        const max = this.$chapterContent.height();
        this.$progressBar.attr('max', max);
    }

    progressBarValue () {
        const value = this.$window.scrollTop() + this.$header.height();
        this.$progressBar.attr('value', value);
    }

    observeNav () {
        const config = {
            rootMargin: '0px 0px -64px'
        };

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.$fixedFooter.addClass('is-fixed');
                this.$chapterContent.addClass('chapter-nav-fixed');
            } else {
                this.$fixedFooter.removeClass('is-fixed');
                this.$chapterContent.removeClass('chapter-nav-fixed');
            }
        }, config);

        observer.observe(this.$chapterContent[0]);
    }

    observeFooter () {
        const footerObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.$fixedFooter.addClass('is-hidden');
            } else {
                this.$fixedFooter.removeClass('is-hidden');
            }
        });

        footerObserver.observe(this.$footerPlaceholder[0]);
    }
}

export default ChapterNav;
